import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import { formatWalletType } from "../../../utils/walletTypes";
import {
  SEQUENCE_FALLBACK,
  isReleaseJobRunning,
  isUnauthorized,
  releaseHintFallback,
} from "./roundReleaseLedgerUtils";
import "../Home/Homepage.css";
import "./ReferralReleasePlansPage.css";

const DEFAULT_ROUNDS = [
  { round: 1, fromMonth: 1, toMonth: 3, percentage: 10 },
  { round: 2, fromMonth: 4, toMonth: 6, percentage: 15 },
  { round: 3, fromMonth: 7, toMonth: 12, percentage: 25 },
  { round: 4, fromMonth: 13, toMonth: 24, percentage: 50 },
];

const emptyForm = () => ({
  name: "",
  setActive: true,
  rounds: DEFAULT_ROUNDS.map((r) => ({ ...r })),
});

const toInt = (v) => {
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : 0;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const roundsToPayload = (rounds) =>
  (rounds || []).map((r, i) => ({
    round: toInt(r.round) || i + 1,
    fromMonth: toInt(r.fromMonth),
    toMonth: toInt(r.toMonth),
    percentage: toInt(r.percentage),
  }));

/** Normalize API rounds for display (handles string/number and missing fields). */
const normalizePlanRounds = (rounds) => {
  if (!Array.isArray(rounds) || !rounds.length) return [];
  return rounds.map((r, i) => ({
    round: toInt(r?.round) || i + 1,
    fromMonth: toInt(r?.fromMonth ?? r?.from_month),
    toMonth: toInt(r?.toMonth ?? r?.to_month),
    percentage: toInt(r?.percentage),
  }));
};

const normalizePlanRow = (p) => ({
  ...p,
  rounds: normalizePlanRounds(p?.rounds),
});

const formatHintWarning = (hint, round, extras = {}) => {
  const fromApi = hint ? String(hint.warning || "").trim() : "";
  return (
    fromApi ||
    releaseHintFallback(round, hint?.percentage ?? extras.percentage, extras.isLast)
  );
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ReferralReleasePlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [formMessage, setFormMessage] = useState({ type: "", text: "" });

  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [actionId, setActionId] = useState(null);

  const [viewPlanId, setViewPlanId] = useState(null);
  const [viewPlan, setViewPlan] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  const [releaseRoundLoading, setReleaseRoundLoading] = useState(null);
  const [releaseRoundMessage, setReleaseRoundMessage] = useState({ type: "", text: "" });
  const [releaseRoundResult, setReleaseRoundResult] = useState(null);
  const [confirmRelease, setConfirmRelease] = useState(null);
  const [mustReleaseRound, setMustReleaseRound] = useState(null);

  const fetchPlans = useCallback(async () => {
    setListLoading(true);
    setListError("");
    try {
      const res = await AdminService.getReferralReleasePlans();
      if (res?.success) {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || res.data?.plans || [];
        setPlans(
          (Array.isArray(raw) ? raw : []).map((row) => normalizePlanRow(row))
        );
      } else {
        setListError(res?.message || "Failed to load plans.");
        setPlans([]);
      }
    } catch (err) {
      setListError(err?.response?.data?.message || err?.message || "Failed to load plans.");
      setPlans([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormMessage({ type: "", text: "" });
    setModal("create");
  };

  const openEdit = async (planId) => {
    setFormMessage({ type: "", text: "" });
    setModal("edit");
    setEditingId(planId);
    setFormLoading(true);
    setForm(emptyForm());
    try {
      const res = await AdminService.getReferralReleasePlanById(planId);
      if (res?.success && res.data) {
        const d = res.data;
        setForm({
          name: String(d.name || ""),
          setActive: false,
          rounds: Array.isArray(d.rounds) && d.rounds.length
            ? d.rounds.map((r) => ({
                round: r.round,
                fromMonth: r.fromMonth,
                toMonth: r.toMonth,
                percentage: r.percentage,
              }))
            : DEFAULT_ROUNDS.map((r) => ({ ...r })),
        });
      } else {
        setFormMessage({ type: "error", text: res?.message || "Failed to load plan." });
        setModal(null);
        setEditingId(null);
      }
    } catch (err) {
      setFormMessage({ type: "error", text: err?.response?.data?.message || err?.message || "Failed to load plan." });
      setModal(null);
      setEditingId(null);
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    if (formSaving) return;
    setModal(null);
    setEditingId(null);
    setFormMessage({ type: "", text: "" });
  };

  const updateName = (name) => {
    setForm((f) => ({ ...f, name }));
    if (formMessage.text) setFormMessage({ type: "", text: "" });
  };

  const updateSetActive = (setActive) => {
    setForm((f) => ({ ...f, setActive }));
  };

  const updateRound = (index, key, value) => {
    setForm((f) => {
      const rounds = [...(f.rounds || [])];
      rounds[index] = { ...rounds[index], [key]: value };
      return { ...f, rounds };
    });
    if (formMessage.text) setFormMessage({ type: "", text: "" });
  };

  const addRound = () => {
    setForm((f) => {
      const rounds = [...(f.rounds || [])];
      const next = rounds.length + 1;
      rounds.push({ round: next, fromMonth: 0, toMonth: 0, percentage: 0 });
      return { ...f, rounds };
    });
  };

  const removeRound = (index) => {
    setForm((f) => {
      const rounds = (f.rounds || []).filter((_, i) => i !== index);
      return { ...f, rounds: rounds.length ? rounds : DEFAULT_ROUNDS.map((r) => ({ ...r })) };
    });
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    const name = String(form.name || "").trim();
    if (!name) {
      setFormMessage({ type: "error", text: "Name is required." });
      return;
    }
    const rounds = roundsToPayload(form.rounds);
    if (!rounds.length) {
      setFormMessage({ type: "error", text: "Add at least one round." });
      return;
    }
    setFormSaving(true);
    setFormMessage({ type: "", text: "" });
    const body = { name, rounds, ...(form.setActive ? { setActive: true } : {}) };
    try {
      const res = await AdminService.createReferralReleasePlan(body);
      if (res?.success) {
        setFormMessage({ type: "success", text: res?.message || "Plan created." });
        await fetchPlans();
        setTimeout(() => {
          closeModal();
        }, 500);
      } else {
        setFormMessage({ type: "error", text: res?.message || "Create failed." });
      }
    } catch (err) {
      setFormMessage({ type: "error", text: err?.response?.data?.message || err?.message || "Create failed." });
    } finally {
      setFormSaving(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    const name = String(form.name || "").trim();
    if (!name) {
      setFormMessage({ type: "error", text: "Name is required." });
      return;
    }
    const rounds = roundsToPayload(form.rounds);
    if (!rounds.length) {
      setFormMessage({ type: "error", text: "Add at least one round." });
      return;
    }
    setFormSaving(true);
    setFormMessage({ type: "", text: "" });
    const body = { name, rounds, ...(form.setActive ? { setActive: true } : {}) };
    try {
      const res = await AdminService.updateReferralReleasePlan(editingId, body);
      if (res?.success) {
        setFormMessage({ type: "success", text: res?.message || "Plan updated." });
        await fetchPlans();
        setTimeout(() => {
          closeModal();
        }, 500);
      } else {
        setFormMessage({ type: "error", text: res?.message || "Update failed." });
      }
    } catch (err) {
      setFormMessage({ type: "error", text: err?.response?.data?.message || err?.message || "Update failed." });
    } finally {
      setFormSaving(false);
    }
  };

  const handleSetActive = async (planId) => {
    setActionId(planId);
    try {
      const res = await AdminService.updateReferralReleasePlan(planId, { setActive: true });
      if (res?.success) await fetchPlans();
      else window.alert(res?.message || "Could not activate plan.");
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Could not activate plan.");
    } finally {
      setActionId(null);
    }
  };

  const handleDeactivate = async (planId) => {
    setActionId(planId);
    try {
      const res = await AdminService.updateReferralReleasePlan(planId, { isActive: false });
      if (res?.success) await fetchPlans();
      else window.alert(res?.message || "Could not deactivate plan.");
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Could not deactivate plan.");
    } finally {
      setActionId(null);
    }
  };

  const openView = async (planId) => {
    setViewPlanId(planId);
    setViewPlan(null);
    setViewError("");
    setViewLoading(true);
    setMustReleaseRound(null);
    setReleaseRoundMessage({ type: "", text: "" });
    setReleaseRoundResult(null);
    try {
      const res = await AdminService.getReferralReleasePlanById(planId);
      if (res?.success && res.data) {
        setViewPlan(normalizePlanRow(res.data));
      } else {
        setViewError(res?.message || "Failed to load plan.");
        setViewPlan(null);
      }
    } catch (err) {
      setViewError(err?.response?.data?.message || err?.message || "Failed to load plan.");
      setViewPlan(null);
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => {
    if (releaseRoundLoading != null) return;
    setViewPlanId(null);
    setViewPlan(null);
    setViewError("");
    setReleaseRoundMessage({ type: "", text: "" });
    setReleaseRoundResult(null);
    setConfirmRelease(null);
    setMustReleaseRound(null);
  };

  const refreshViewPlan = async (planId) => {
    const id = planId || viewPlan?._id || viewPlan?.id || viewPlanId;
    if (!id) return;
    try {
      const res = await AdminService.getReferralReleasePlanById(id);
      if (res?.success && res.data) setViewPlan(normalizePlanRow(res.data));
    } catch {
      /* keep current plan */
    }
  };

  const applyReleaseResult = (d, message, type = "success") => {
    const currency = d?.currency || "USDT";
    const amountLine =
      d?.total_amount_released != null
        ? `${d.total_amount_released} ${currency} credited to staking wallet.`
        : "";
    const extra =
      d?.releasedRounds != null || d?.skipped != null
        ? `${d.releasedRounds ?? 0} users credited, ${d.skipped ?? 0} skipped.`
        : "";
    const uiMessage = d?.sequence?.ui_message || "";
    setReleaseRoundResult(d || null);
    setReleaseRoundMessage({
      type,
      text: [message, extra, amountLine, uiMessage].filter(Boolean).join(" "),
    });
  };

  const pollReleaseUntilIdle = async () => {
    for (let i = 0; i < 40; i += 1) {
      await sleep(2000);
      const st = await AdminService.getReferralReleaseRoundNowStatus();
      if (isUnauthorized(st)) {
        window.location.href = "/";
        return null;
      }
      if (isReleaseJobRunning(st)) continue;
      return st?.data?.result && typeof st.data.result === "object"
        ? { ...st.data, ...st.data.result, message: st.message }
        : st?.data || st;
    }
    return { still_running: true };
  };

  const handleReleaseRound = async (roundNum) => {
    if (!viewPlan) return;
    const rn = toInt(roundNum);
    setReleaseRoundLoading(rn);
    setReleaseRoundMessage({ type: "", text: "" });
    setReleaseRoundResult(null);
    setMustReleaseRound(null);
    try {
      const res = await AdminService.releaseReferralRoundNow({
        round: rn,
        ignoreMonthWindow: true,
      });
      const d = res?.data && typeof res.data === "object" ? res.data : {};
      const http = Number(res?.httpStatus);
      const inProgress = http === 409 || d.in_progress === true;

      if (inProgress) {
        const failText =
          res?.message || "Round release is already in progress. Please wait and refresh the Round Release Ledger.";
        toast.error(failText);
        setReleaseRoundMessage({ type: "error", text: failText });
        setConfirmRelease(null);
        const finished = await pollReleaseUntilIdle();
        if (finished?.still_running) {
          toast("Still processing. Refresh the Round Release Ledger in a few minutes.", { icon: "⏳" });
        } else if (finished) {
          applyReleaseResult(finished, finished.message || "Release finished.");
          toast.success(finished.message || "Release finished.");
          await refreshViewPlan();
        }
        return;
      }

      if (!res?.success) {
        const failText = res?.message || "Release failed.";
        toast.error(failText);
        setReleaseRoundMessage({ type: "error", text: failText });
        if (d.must_release_round != null) setMustReleaseRound(toInt(d.must_release_round));
        setConfirmRelease(null);
        return;
      }

      toast.success(res.message || "Release completed", { duration: 6000 });
      setConfirmRelease(null);

      if (isReleaseJobRunning(res) || String(d.status || "").toLowerCase() === "processing") {
        setReleaseRoundMessage({ type: "success", text: res.message || "Release started…" });
        const finished = await pollReleaseUntilIdle();
        if (finished?.still_running) {
          toast("Still processing. Refresh the Round Release Ledger in a few minutes.", { icon: "⏳" });
        } else if (finished) {
          applyReleaseResult(finished, finished.message || res.message || "Release completed.");
          toast.success(finished.message || "Release completed.");
        }
        await refreshViewPlan();
        return;
      }

      applyReleaseResult(d, res.message || "Release completed.");
      await refreshViewPlan();
    } catch (err) {
      const failText = err?.response?.data?.message || err?.message || "Release failed.";
      toast.error(failText);
      setReleaseRoundMessage({ type: "error", text: failText });
    } finally {
      setReleaseRoundLoading(null);
    }
  };

  const openEditFromView = () => {
    const id = viewPlan?._id || viewPlan?.id || viewPlanId;
    closeView();
    if (id) void openEdit(id);
  };

  const openDeleteFromView = () => {
    const id = viewPlan?._id || viewPlan?.id || viewPlanId;
    if (id) {
      setDeleteId(id);
      closeView();
    }
  };

  const setActiveFromView = async () => {
    const id = viewPlan?._id || viewPlan?.id || viewPlanId;
    if (!id) return;
    closeView();
    await handleSetActive(id);
  };

  const deactivateFromView = async () => {
    const id = viewPlan?._id || viewPlan?.id || viewPlanId;
    if (!id) return;
    closeView();
    await handleDeactivate(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      const res = await AdminService.deleteReferralReleasePlan(deleteId);
      if (res?.success) {
        setDeleteId(null);
        await fetchPlans();
      } else {
        window.alert(res?.message || "Delete failed.");
      }
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.message || "Delete failed.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage referral-release-plans-page">
      <section className="dashboard-hero referral-plans-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Referral Release Plans</span>
          </p>
          <h1 className="dashboard-hero__title">Referral Release Plans</h1>
          <p className="dashboard-hero__subtitle">Create and edit month-range release rounds for referrals</p>
        </div>
        <div className="referral-plans-hero__icon" aria-hidden>
          <i className="fas fa-project-diagram" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="referral-plans-toolbar">
          <button type="button" className="referral-plans-btn-primary" onClick={openCreate}>
            <i className="fas fa-plus" /> Create plan
          </button>
        </div>

        <section className="referral-plans-card">
          <h2 className="referral-plans-card__title">Plans</h2>
          {listLoading ? (
            <div className="referral-plans-loading">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : listError ? (
            <p className="referral-plans-msg referral-plans-msg--error">{listError}</p>
          ) : plans.length === 0 ? (
            <p className="referral-plans-empty">No plans yet. Create one to get started.</p>
          ) : (
            <div className="referral-plans-table-wrap">
              <table className="referral-plans-table">
                <thead>
                  <tr>
                    <th>Plan name</th>
                    <th>Created</th>
                    <th>Active</th>
                    <th className="referral-plans-table__actions-col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => {
                    const rid = p._id || p.id;
                    return (
                    <tr key={rid}>
                      <td className="referral-plans-table__name">{p.name || "—"}</td>
                      <td className="referral-plans-table__created">{formatDate(p.createdAt)}</td>
                      <td>
                        {p.isActive ? (
                          <span className="referral-plans-badge referral-plans-badge--on">Yes</span>
                        ) : (
                          <span className="referral-plans-badge referral-plans-badge--off">No</span>
                        )}
                      </td>
                      <td className="referral-plans-table__actions-col">
                        <button
                          type="button"
                          className="referral-plans-btn-view"
                          onClick={() => openView(rid)}
                          disabled={actionId === rid}
                        >
                          <i className="fas fa-eye" /> View
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {(modal === "create" || modal === "edit") && (
        <div className="referral-plans-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="rrp-modal-title">
          <div className="referral-plans-modal">
            <div className="referral-plans-modal__head">
              <h2 id="rrp-modal-title">{modal === "create" ? "Create plan" : "Edit plan"}</h2>
              <button type="button" className="referral-plans-modal__close" onClick={closeModal} disabled={formSaving} aria-label="Close">
                <i className="fas fa-times" />
              </button>
            </div>
            {formLoading ? (
              <div className="referral-plans-loading" style={{ padding: "1.5rem" }}>
                <i className="fas fa-spinner fa-spin" /> Loading plan…
              </div>
            ) : (
              <form onSubmit={modal === "create" ? handleSubmitCreate : handleSubmitEdit} className="referral-plans-form">
                <div className="referral-plans-form__row">
                  <label htmlFor="rrp-name">Name</label>
                  <input
                    id="rrp-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => updateName(e.target.value)}
                    placeholder="e.g. 2026 Q1"
                    required
                    autoFocus
                  />
                </div>
                <div className="referral-plans-form__row referral-plans-form__check">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.setActive}
                      onChange={(e) => updateSetActive(e.target.checked)}
                    />
                    {modal === "create"
                      ? "Activate this plan (deactivates other plans)"
                      : "Set as only active plan on save (deactivates others)"}
                  </label>
                </div>

                <p className="referral-plans-form__hint">Rounds: month ranges and release percentage. Send the full set when saving; order should match your release schedule.</p>

                <div className="referral-plans-rounds-head">
                  <span>Rounds</span>
                  <button type="button" className="referral-plans-linkbtn" onClick={addRound}>
                    + Add round
                  </button>
                </div>
                <div className="referral-plans-rounds-table-wrap">
                  <table className="referral-plans-rounds-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>From month</th>
                        <th>To month</th>
                        <th>%</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(form.rounds || []).map((r, i) => (
                        <tr key={i}>
                          <td>
                            <input
                              className="referral-plans-rounds-input"
                              type="number"
                              value={r.round}
                              onChange={(e) => updateRound(i, "round", toInt(e.target.value))}
                              min={1}
                            />
                          </td>
                          <td>
                            <input
                              className="referral-plans-rounds-input"
                              type="number"
                              value={r.fromMonth}
                              onChange={(e) => updateRound(i, "fromMonth", toInt(e.target.value))}
                              min={0}
                            />
                          </td>
                          <td>
                            <input
                              className="referral-plans-rounds-input"
                              type="number"
                              value={r.toMonth}
                              onChange={(e) => updateRound(i, "toMonth", toInt(e.target.value))}
                              min={0}
                            />
                          </td>
                          <td>
                            <input
                              className="referral-plans-rounds-input"
                              type="number"
                              value={r.percentage}
                              onChange={(e) => updateRound(i, "percentage", toInt(e.target.value))}
                              min={0}
                              max={100}
                            />
                          </td>
                          <td>
                            <button type="button" className="referral-plans-linkbtn referral-plans-linkbtn--danger" onClick={() => removeRound(i)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {formMessage.text ? (
                  <p className={`referral-plans-msg referral-plans-msg--${formMessage.type || "error"}`}>{formMessage.text}</p>
                ) : null}

                <div className="referral-plans-modal__footer">
                  <button type="button" className="referral-plans-btn-secondary" onClick={closeModal} disabled={formSaving}>
                    Cancel
                  </button>
                  <button type="submit" className="referral-plans-btn-primary" disabled={formSaving}>
                    <i className={`fas ${formSaving ? "fa-spinner fa-spin" : "fa-check"}`} />
                    {formSaving ? "Saving…" : modal === "create" ? "Create" : "Save changes"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {viewPlanId && (
        <div className="referral-plans-modal-overlay referral-plans-modal-overlay--view" role="dialog" aria-modal="true" aria-labelledby="rrp-view-title">
          <div className="referral-plans-modal referral-plans-modal--view">
            <div className="referral-plans-modal__head referral-plans-modal__head--view">
              <div className="rrp-view-head">
                <p className="referral-plans-view-head-eyebrow">Referral release plan</p>
                <div className="rrp-view-head__row">
                  <h2 id="rrp-view-title">
                    {viewPlan?.name ? String(viewPlan.name) : "Plan details"}
                  </h2>
                  {viewPlan ? (
                    <span
                      className={`referral-plans-pill ${
                        viewPlan.isActive
                          ? "referral-plans-pill--active rrp-view-pill--on-dark"
                          : "referral-plans-pill--inactive rrp-view-pill--on-dark"
                      }`}
                    >
                      {viewPlan.isActive ? "Active" : "Inactive"}
                    </span>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className="referral-plans-modal__close referral-plans-modal__close--on-dark"
                onClick={closeView}
                disabled={viewLoading || releaseRoundLoading != null}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="referral-plans-view-body referral-plans-view-body--premium">
              {viewLoading ? (
                <div className="referral-plans-loading referral-plans-view-loading">
                  <i className="fas fa-spinner fa-spin" /> Loading plan…
                </div>
              ) : viewError ? (
                <p className="referral-plans-msg referral-plans-msg--error">{viewError}</p>
              ) : viewPlan ? (
                <>
                  <div className="rrp-view-meta">
                    <div className="rrp-view-meta__item">
                      <i className="far fa-calendar-alt" aria-hidden />
                      <div>
                        <span className="rrp-view-meta__label">Created</span>
                        <span className="rrp-view-meta__value">{formatDate(viewPlan.createdAt)}</span>
                      </div>
                    </div>
                    <div className="rrp-view-meta__item">
                      <i className="fas fa-wallet" aria-hidden />
                      <div>
                        <span className="rrp-view-meta__label">Pays to</span>
                        <span className="rrp-view-meta__value">
                          {formatWalletType(
                            viewPlan.destination_wallet || viewPlan.sequence?.wallet_type || "staking"
                          )}{" "}
                          wallet
                        </span>
                      </div>
                    </div>
                    <div className="rrp-view-meta__item">
                      <i className="fas fa-layer-group" aria-hidden />
                      <div>
                        <span className="rrp-view-meta__label">Schedule</span>
                        <span className="rrp-view-meta__value">
                          {(viewPlan.rounds || []).length} round{(viewPlan.rounds || []).length === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="referral-plans-sequence-note" role="note">
                    <i className="fas fa-info-circle" aria-hidden />
                    <div>
                      <strong>How release works</strong>
                      <p>{SEQUENCE_FALLBACK}</p>
                      {viewPlan.sequence?.rule &&
                      viewPlan.sequence.rule !== SEQUENCE_FALLBACK ? (
                        <p>{viewPlan.sequence.rule}</p>
                      ) : null}
                      <p className="referral-plans-sequence-note__dest">
                        {viewPlan.sequence?.destination ||
                          "Funds go to the staking wallet only (staking purchase only — no transfer, no withdrawal)."}
                      </p>
                    </div>
                  </div>

                  <section className="referral-plans-view-rounds-block">
                    <div className="referral-plans-view-rounds-block__head">
                      <h3>Release schedule</h3>
                      <p>
                        Each user&apos;s total reward is split by plan % (last round leftover).
                        One credit per user per round, to the staking wallet only.
                      </p>
                    </div>
                    {releaseRoundMessage.text ? (
                      <p
                        className={`referral-plans-round-release-msg referral-plans-round-release-msg--${releaseRoundMessage.type || "error"}`}
                        role="status"
                      >
                        {releaseRoundMessage.text}
                      </p>
                    ) : null}
                    {releaseRoundResult?.skip_breakdown ? (
                      <ul className="referral-plans-skip-breakdown">
                        {releaseRoundResult.skip_breakdown.previous_round_pending ? (
                          <li>
                            Previous round pending: {releaseRoundResult.skip_breakdown.previous_round_pending}
                          </li>
                        ) : null}
                        {releaseRoundResult.skip_breakdown.already_released ? (
                          <li>
                            Already released: {releaseRoundResult.skip_breakdown.already_released}
                          </li>
                        ) : null}
                        {releaseRoundResult.skip_breakdown.month_window_not_reached ? (
                          <li>
                            Month window not reached: {releaseRoundResult.skip_breakdown.month_window_not_reached}
                          </li>
                        ) : null}
                        {releaseRoundResult.skip_breakdown.already_logged ? (
                          <li>
                            Already logged: {releaseRoundResult.skip_breakdown.already_logged}
                          </li>
                        ) : null}
                      </ul>
                    ) : null}
                    {!(viewPlan.rounds && viewPlan.rounds.length) ? (
                      <p className="referral-plans-empty referral-plans-empty--in-card">No rounds on this plan.</p>
                    ) : (
                      <ol className="rrp-round-list">
                        {viewPlan.rounds.map((r, idx) => {
                          const roundKey = toInt(r.round) || idx + 1;
                          const isLoading = releaseRoundLoading === roundKey;
                          const hints = Array.isArray(viewPlan.release_button_hints)
                            ? viewPlan.release_button_hints
                            : [];
                          const hint = hints.find((h) => toInt(h.round) === roundKey);
                          const isLast = idx === viewPlan.rounds.length - 1;
                          const warning = formatHintWarning(hint, roundKey, {
                            isLast,
                            percentage: hint?.percentage ?? r.percentage,
                          });
                          const apiLocked = hint?.can_click === false;
                          const canClick = !apiLocked && releaseRoundLoading == null;
                          const mustFirst = mustReleaseRound != null && mustReleaseRound === roundKey;
                          return (
                            <li
                              key={`v-${r.round}-${idx}`}
                              className={`rrp-round ${apiLocked ? "rrp-round--locked" : ""} ${mustFirst ? "rrp-round--must-release" : ""}`}
                            >
                              <span className="rrp-round__num" aria-hidden>
                                {roundKey}
                              </span>
                              <div className="rrp-round__body">
                                <div className="rrp-round__top">
                                  <div>
                                    <p className="rrp-round__title">Round {roundKey}</p>
                                    <p className="rrp-round__window">
                                      Months {r.fromMonth}–{r.toMonth}
                                    </p>
                                  </div>
                                  <span className="rrp-round__pct">
                                    {r.percentage}
                                    <small>%</small>
                                    {isLast ? <small className="rrp-round__left"> leftover</small> : null}
                                  </span>
                                </div>
                                <div className="rrp-round__action">
                                  <button
                                    type="button"
                                    className={[
                                      "referral-plans-btn-release",
                                      isLoading ? "referral-plans-btn-release--loading" : "",
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                    onClick={() =>
                                      setConfirmRelease({
                                        round: roundKey,
                                        percentage: r.percentage,
                                        fromMonth: r.fromMonth,
                                        toMonth: r.toMonth,
                                        warning,
                                        isLast,
                                      })
                                    }
                                    disabled={!canClick || isLoading || releaseRoundLoading != null}
                                    title={warning || "Run manual release for this round"}
                                  >
                                    {isLoading ? (
                                      <>
                                        <i className="fas fa-spinner fa-spin" /> Releasing…
                                      </>
                                    ) : apiLocked ? (
                                      "Locked"
                                    ) : (
                                      "Release"
                                    )}
                                  </button>
                                  {warning ? (
                                    <p className="referral-plans-release-hint">
                                      <i className="fas fa-info-circle" aria-hidden /> {warning}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    )}
                  </section>
                </>
              ) : null}
            </div>
            <div className="referral-plans-modal__footer referral-plans-view-footer">
              <div className="referral-plans-view-footer__main">
                <button type="button" className="referral-plans-btn-secondary" onClick={closeView} disabled={releaseRoundLoading != null}>
                  Close
                </button>
                {!viewLoading && !viewError && viewPlan && (
                  <>
                    <button
                      type="button"
                      className="referral-plans-btn-primary"
                      onClick={openEditFromView}
                      disabled={viewLoading}
                    >
                      <i className="fas fa-edit" /> Edit
                    </button>
                    {!viewPlan.isActive && (
                      <button
                        type="button"
                        className="referral-plans-btn-primary"
                        onClick={setActiveFromView}
                        disabled={viewLoading}
                        title="Sets this plan active; other plans are deactivated"
                      >
                        Set active
                      </button>
                    )}
                    {viewPlan.isActive && (
                      <button
                        type="button"
                        className="referral-plans-btn-secondary"
                        onClick={deactivateFromView}
                        disabled={viewLoading}
                      >
                        Deactivate
                      </button>
                    )}
                    <button
                      type="button"
                      className="referral-plans-btn-danger"
                      onClick={openDeleteFromView}
                      disabled={viewLoading}
                    >
                      <i className="fas fa-trash-alt" /> Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmRelease && (
        <div
          className="referral-plans-modal-overlay referral-plans-modal-overlay--confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rrp-release-title"
          onClick={() => releaseRoundLoading == null && setConfirmRelease(null)}
        >
          <div
            className="referral-plans-modal referral-plans-modal--sm referral-plans-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="referral-plans-confirm__icon" aria-hidden>
              <i className="fas fa-exclamation-triangle" />
            </div>
            <h2 id="rrp-release-title" className="referral-plans-modal__head-text">
              Release round {confirmRelease.round}?
            </h2>
            <p className="referral-plans-del-text">
              This credits each eligible user&apos;s{" "}
              <strong>
                {confirmRelease.percentage != null
                  ? `${confirmRelease.percentage}% of total reward`
                  : "plan % of total reward"}
              </strong>{" "}
              for round {confirmRelease.round} to the staking wallet — one credit per user.
              {confirmRelease.isLast
                ? " Last round pays leftover so the total is 100%."
                : ""}
              {confirmRelease.fromMonth != null && confirmRelease.toMonth != null
                ? ` Month window: ${confirmRelease.fromMonth}–${confirmRelease.toMonth}.`
                : ""}
            </p>
            <p className="referral-plans-confirm__warn">
              {confirmRelease.warning ||
                releaseHintFallback(
                  confirmRelease.round,
                  confirmRelease.percentage,
                  confirmRelease.isLast
                )}{" "}
              This cannot be undone for users who get paid.
            </p>
            <div className="referral-plans-modal__footer">
              <button
                type="button"
                className="referral-plans-btn-secondary"
                onClick={() => setConfirmRelease(null)}
                disabled={releaseRoundLoading != null}
              >
                Cancel
              </button>
              <button
                type="button"
                className="referral-plans-btn-release"
                onClick={() => handleReleaseRound(confirmRelease.round)}
                disabled={releaseRoundLoading != null}
              >
                {releaseRoundLoading != null ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Releasing…
                  </>
                ) : (
                  "Confirm release"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="referral-plans-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="rrp-del-title">
          <div className="referral-plans-modal referral-plans-modal--sm">
            <h2 id="rrp-del-title" className="referral-plans-modal__head-text">Delete plan?</h2>
            <p className="referral-plans-del-text">This action cannot be undone.</p>
            <div className="referral-plans-modal__footer">
              <button type="button" className="referral-plans-btn-secondary" onClick={() => setDeleteId(null)} disabled={deleteLoading}>
                Cancel
              </button>
              <button type="button" className="referral-plans-btn-danger" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? "…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ReferralReleasePlansPage;
